<script setup lang="ts">
import { checkCorrectValuePrice } from '@/utils/checkCorrectValuePrice'
import { defineLayoutOfProduct } from '@/utils/defineLayoutOfProduct'

import { useMenuByPointsDialog } from '@/composables/menu-by-points/useMenuByPointsDialog'
import { useProductCounter } from '@/composables/useProductCounter'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'
import { useToggleStore } from '@/store/toggle/toggle'

import type { Modification, Product } from '@/types/api/api-v3/product'
import type {
  OneProductV5,
  ProductV5Modification,
} from '@/types/api/api-v5/product'
import { EcommerceEventName } from '@/types/plugins/yandex-metrika'
import { SetProductIncludeType, SetProductType } from '@/types/store/category'

import AppMeasureUnits from '@/components/App/AppMeasureUnits.vue'
import ProductButtonPage from '@/components/Product/ProductItem/ProductContent/ProductButtonPage.vue'
import ProductCalories from '@/components/Product/ProductItem/ProductContent/ProductCalories.vue'
import ProductCompound from '@/components/Product/ProductItem/ProductContent/ProductCompound.vue'
import ProductModifications from '@/components/Product/ProductItem/ProductContent/ProductModifications.vue'

type Props = {
  product: Product | OneProductV5
  currentModification: Modification | ProductV5Modification
  parentComponent: string
  isAvailable: boolean
}
const props = defineProps<Props>()

const emit = defineEmits(['changeModification'])

const { $metrics } = useNuxtApp()

const { menuByPointsEnabled } = useToggleStore()
const cityStore = useCityStore()
const basketStore = useBasketStore()
const { showSnackbar } = useAppStore()

const { isDesktop } = useDevice()

const isTitleProduct = computed(() => {
  if (props.product.modifications.length >= 2) return `${props.product.name}`
  else
    return props.product.modifications[0].mode
      ? `${props.product.name}: ${props.product.modifications[0].mode}`
      : `${props.product.name}`
})

const isHighLight = computed(() => {
  if (menuByPointsEnabled) {
    const product = props.product as OneProductV5
    return product.main_tags.find((item) => item.as_highlight)
  }

  const product = props.product as Product
  return product.tags.find((item) => item.as_highlight)
})

const currencyCode = computed(() => cityStore.currencyCode)

const quantityInProductBasket = computed(() => {
  return (
    basketStore.isProductInBasket(props.currentModification.id)?.quantity || 0
  )
})

const productId = computed(() => props.currentModification.id)

const isCompoundVisible = computed(() => {
  return props.product?.marking_composition
})

const currentModification = computed(() => props.currentModification)

const {
  basketError,
  quantitySelectedProducts,
  setProductQuantity,
  decreaseQuantity,
  increaseQuantity,
} = useProductCounter(productId, currentModification)

const { showMenuByPointsDialogs } = useMenuByPointsDialog()

const { pushMetrics } = useSendMetricsOfProduct(props.currentModification)

const onUpdateProductQuantity = () => {
  const quantity = quantityInProductBasket.value

  setProductQuantity(quantity)
}

const changeModification = (modificationId: number) => {
  // await, чтобы работать дальше с измененным currentModification
  emit('changeModification', modificationId)

  // ищем кол- во товара по модификации в корзине и меняем в случае, если он есть
  setProductQuantity(quantityInProductBasket.value)
}

const addToCart = async (productId: number) => {
  // Клик по кнопке "добавить в корзину" в меню
  $metrics?.eventSendGoal({ ym: 'click_add_to_basket' })

  if (showMenuByPointsDialogs()) return

  setProductQuantity(quantitySelectedProducts.value + 1) // текущее + 1

  await basketStore.addProduct(productId, +props?.currentModification.price)

  if (!basketError.value) {
    pushMetrics(EcommerceEventName.ADD, 1)

    return
  }

  setProductQuantity(quantityInProductBasket.value) // при ошибке возвращаем кол-во с сервера

  showSnackbar({
    message: 'Что-то пошло не так',
    type: SnackbarType.MESSAGE,
  })
}

const setProduct = (type: SetProductIncludeType) => {
  // Проверяем, какая кнопка counter-а была нажата и высчитываем измененнное кол- во продукта
  if (type === SetProductType.DECREASE) {
    decreaseQuantity()
    return
  }

  increaseQuantity()
}

const getProductMicrolayouts = defineLayoutOfProduct(props.parentComponent)

const itemPropProductTitle = getProductMicrolayouts('name')
const itemPropProductDescription = getProductMicrolayouts('description')

const showOldPrice = computed(() => {
  if (typeof props.currentModification.subprice === 'string') {
    return checkCorrectValuePrice(props.currentModification.subprice)
  }

  return props.currentModification.subprice > 0
})
</script>

<template>
  <div class="product__content">
    <div class="product__content-title">
      <h1 class="product__content-headline" :itemprop="itemPropProductTitle">
        {{ isTitleProduct }}
      </h1>
    </div>

    <div v-if="isHighLight" class="product__content-sale">
      <span
        class="product__content-sale--text"
        :style="{ 'background-color': isHighLight.hover_background_color }"
      >
        {{ isHighLight.name }}
      </span>
    </div>

    <div class="product__content-description">
      <div
        :itemprop="itemPropProductDescription"
        v-html="currentModification.description"
      />
    </div>

    <product-compound
      v-if="isCompoundVisible"
      :marking-composition="product?.marking_composition"
      class="product__content-compound"
    />

    <product-calories
      class="product__content-calories"
      :is-product-page="isDesktop"
      :nutritional-value="currentModification.nutritional_values"
    />

    <div
      v-if="product.modifications.length >= 2"
      class="product__content-modifications"
      tabindex="-1"
    >
      <product-modifications
        :current-modification-id="currentModification.id"
        :modifications="product.modifications"
        @change-modification="changeModification"
      />
    </div>

    <div
      v-for="modificationItem in product.modifications"
      :key="`offer_${modificationItem.id}`"
      class="hidden"
      itemprop="offers"
      itemscope
      itemtype="http://schema.org/Offer"
    >
      <meta itemprop="price" :content="`${modificationItem.price}`" />
      <meta itemprop="priceCurrency" :content="currencyCode" />
    </div>

    <div class="product__content-price">
      <app-price
        :class="{
          'product__content-price--new': currentModification.subprice,
        }"
        tag="p"
        :price="currentModification.price"
      />

      <app-price
        v-if="showOldPrice"
        class="product__content-price--discount"
        tag="div"
        :price="currentModification.subprice"
      />
    </div>

    <div class="product__content-actions">
      <the-button
        v-if="!isAvailable"
        :size="!isDesktop ? 'sm' : 'md'"
        disabled
        variant="tertiary"
      >
        Нет в наличии
      </the-button>

      <the-button
        v-else-if="!basketStore.isLoaded"
        class="button--loading"
        loading
        disabled
        radius="lg"
        :size="!isDesktop ? 'sm' : 'md'"
      />

      <product-button-page
        v-else
        :product-quantity="quantitySelectedProducts"
        @add-to-cart="addToCart(currentModification.id)"
        @set-increase-product="setProduct(SetProductType.INCREASE)"
        @set-decrease-product="setProduct(SetProductType.DECREASE)"
        @update-product-quantity="onUpdateProductQuantity"
      />
    </div>

    <div class="product__content-weight">
      <app-measure-units
        :weight="currentModification.weight"
        :volume-in-liters="currentModification.volume_in_liters"
        :measure="currentModification.measure"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.product__content {
  display: grid;
  grid-template-areas:
    'title title'
    'sale sale'
    'description description'
    'compound compound'
    'calories calories'
    'modifications modifications'
    'price actions'
    'weight actions';

  @include media('>=tablet', '<desktop') {
    grid-template-areas:
      'sale sale'
      'title title'
      'modifications modifications'
      'price actions'
      'weight actions'
      'compound compound'
      'description description'
      'calories calories';
  }

  @include media('<tablet') {
    grid-template-areas:
      'sale sale'
      'title title'
      'description description'
      'modifications modifications'
      'price actions'
      'weight actions'
      'compound compound'
      'calories calories';
  }

  &-components {
    grid-area: components;
  }

  &-headline {
    display: inline;
  }

  &-title {
    grid-area: title;
    margin-bottom: 24px;
    font-size: 22px;
    font-weight: 800;

    h1 {
      font-size: 22px;
      font-weight: 800;
    }

    @include media('<desktop') {
      margin-bottom: 16px;
      font-size: 16px;

      h1 {
        font-size: 16px;
      }
    }
  }

  &-description {
    grid-area: description;
    margin: 40px 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 19px;
    color: var(--ui-dark-grey);

    @include media('<desktop') {
      margin: 24px 0;
      font-size: 14px;
    }

    @include media('<tablet') {
      margin-bottom: 24px;
      font-size: 12px;
    }
  }

  &-sale {
    grid-area: sale;
    width: max-content;

    @include media('<=desktop') {
      margin-bottom: 24px;
    }

    &--text {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 5px 10px;
      font-size: 12px;
      font-weight: 700;
      line-height: 14px;
      color: var(--ui-white);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      background: #00c304;
      border-radius: 20px;
    }
  }

  &-calories {
    grid-area: calories;
    max-width: 80%;
    margin-bottom: 40px;
    font-size: 22px !important;

    @include media('<desktop') {
      max-width: 260px;
      margin: 0;
    }

    @include media('<tablet') {
      margin-top: 24px;
    }
  }

  &-modifications {
    grid-area: modifications;
    margin-bottom: 40px;

    @include media('<desktop') {
      margin-bottom: 24px;
    }
  }

  &-compound {
    grid-area: compound;
    margin-bottom: 20px;
  }

  &-actions {
    display: flex;
    grid-area: actions;
    align-items: center;
    justify-content: flex-end;

    .button--loading {
      width: 177px;

      @include media('<desktop-mini') {
        width: 108px;
      }
    }
  }

  &-price {
    display: flex;
    grid-area: price;
    align-items: center;

    &--new {
      color: var(--ui-primary);
    }

    p {
      margin: 0;
      font-size: 28px;
      font-weight: 800;
      line-height: 33px;

      @include media('<desktop') {
        font-size: 22px;
        line-height: 26px;
      }
    }

    &--discount {
      margin-left: 10px;
      font-size: 20px;
      font-weight: 600;
      line-height: 23px;
      text-decoration: line-through;

      @include media('<desktop') {
        font-size: 16px;
        line-height: 19px;
      }
    }
  }

  &-weight {
    display: flex;
    grid-area: weight;
    align-items: center;
    margin-top: 10px;
    font-size: 16px;
    font-weight: 600;
    color: var(--ui-dark-grey);

    @include media('<desktop') {
      font-size: 14px;
    }
  }
}

.hidden {
  display: none;
}
</style>
